import { AfterViewInit, Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { NavBarComponent } from '../../../shared/components/nav-bar/nav-bar.component';
import { CommonModule } from '@angular/common';
import { CardComponent } from '../../../shared/components/card/card.component';
import { Router, RouterModule } from '@angular/router';
import { register } from 'swiper/element/bundle';
import { SwiperContainer } from 'swiper/element';
import { ApiService } from '../../../services/api.service';
import { HttpErrorResponse } from '@angular/common/http';
import { marketAPIFilterInput } from '../../../common/interface/common.interface';
import { StorageService } from '../../../services/storage.service';
import { EnableModalComponent } from '../../../shared/components/enable-modal/enable-modal.component';
import { ChartComponent } from '../chart/chart.component';
import { FormsModule } from '@angular/forms';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { OnDestroy } from '@angular/core';
import { AuthService } from '../../../services/auth.service';
import { BookmarkService } from '../../../services/bookmark.service';
import { MarketSortKey, MARKET_SORT_KEY_MAP, HOME_FILTER_DROPDOWNS, MarketFrequency, MARKET_FREQUENCY_MAP, MarketType, MARKET_TYPE_MAP } from '../../../common/constants/common.constants';
import { measureDropdownPlacement, PageScrollLock } from '../../../common/utils/dropdown-viewport';

/** Tallest a filter list gets when there is room to spare */
const FILTER_MENU_CAP = 280;
/** Gap between the trigger and the list hanging off it */
const FILTER_MENU_GAP = 4;
/** Width the list is laid out at, used to keep it inside the screen */
const FILTER_MENU_WIDTH = 180;
/** Breathing room kept between the list and the side of the screen */
const FILTER_MENU_EDGE_GAP = 12;

// Register Swiper web component
register();
@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, NavBarComponent, ChartComponent, CardComponent, RouterModule, EnableModalComponent, FormsModule],
  templateUrl: './home.component.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  styleUrl: './home.component.css'
})
export class HomeComponent implements OnInit, AfterViewInit {
  tagsList: Array<any> = [];
  selectedCategoryID: string = "";
  selectedTagIDs: Array<string> = ["all"];
  cardList: Array<any> = [];
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;
  @ViewChild('marketSearchInput') marketSearchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('cardsSwiper', { static: false }) cardsSwiper!: ElementRef<SwiperContainer>;
  @ViewChild('marketsContainer') marketsContainer!: ElementRef;
  @ViewChild('filterScrollContainer') filterScrollContainer?: ElementRef<HTMLElement>;
  showLeftArrow = false;
  showRightArrow = true;
  showFilterLeftArrow = false;
  showFilterRightArrow = false;
  // The filter row scrolls horizontally, so its dropdown is positioned as a fixed
  // overlay against the trigger instead of being clipped inside the scroller.
  // It is anchored by its top edge normally, and by its bottom edge when there
  // is more room above the trigger than below it.
  homeFilterDropdownPosition: { top: number | null; bottom: number | null; left: number; maxHeight: number } | null = null;
  private scrollLock = new PageScrollLock();
  swiperInstance: any;
  isSwiperInitialized = false;
  currentCardSlide = 0;
  totalCardSlides = 3;
  showCardSliderArrows = true;
  showEnableModal = false;
  cardRows: any[] = [];
  searchKey: string = "";
  searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  isFilterPanelOpen = false;
  openHomeFilterDropdownKey: 'volume' | 'frequency' | 'status' | null = null;
  isBookmarkFilterActive = false;

  // <= 575px only: while the mobile tab bar's "Search" tab is driving the search
  // field, the field takes the whole row and the two icon buttons step aside.
  isMobileSearchActive: boolean = false;
  selectedSort: MarketSortKey = MarketSortKey.NEWEST;
  selectedFrequency: MarketFrequency | null = null;
  selectedMarketType: MarketType | null = MarketType.ACTIVE;
  homeFilterDropdowns = HOME_FILTER_DROPDOWNS.map(d => ({ ...d }));
  // homeFilterToggles = [
  //   { key: 'sports' as const, label: 'Hide sports', checked: false },
  //   { key: 'crypto' as const, label: 'Hide crypto', checked: false },
  //   { key: 'earnings' as const, label: 'Hide earnings', checked: false }
  // ];

  // Feature highlight carousel (mobile only — desktop shows all three in a grid)
  readonly featureSlides = [0, 1, 2];
  readonly FEATURE_SLIDE_INTERVAL = 3000;
  featureSlide = 0;
  private featureTimer: any = null;

  // Pagination properties
  PAGE_SIZE: number = 20;
  marketsPage: number = 1;
  hasMoreMarkets: boolean = true;
  isLoadingMarkets: boolean = false;

  ngAfterViewInit() {
    setTimeout(() => {
      this.updateArrows();
      this.setupSwiperListeners();
      if (this.cardsSwiper && this.cardsSwiper.nativeElement) {
        this.swiperInstance = (this.cardsSwiper.nativeElement as any).swiper;
      }
    }, 100);
    this.startFeatureCarousel();

    // Honours a search focus request made from another page, where this input
    // did not exist yet at the time of the request.
    if (this.storage.consumeSearchFocusRequest()) {
      this.focusSearchInput();
    }

    this.searchSubject.pipe(
      debounceTime(300),
      takeUntil(this.destroy$) // Good practice to prevent leaks
    ).subscribe((searchValue) => {
      this.searchKey = searchValue; // Ensure local variable is updated
      this.getMarkets();
    });

  }

  swiperBreakpoints = {
    640: { slidesPerView: 1 },
    768: { slidesPerView: 1 },
    1024: { slidesPerView: 1 },
    1280: { slidesPerView: 1 }
  };

  // Add a flag to track if swiper is ready
  isSwiperReady = false;

  constructor(
    private router: Router,
    private service: ApiService,
    private storage: StorageService,
    private auth: AuthService,
    public bookmarkService: BookmarkService
  ) {
    // Ensure swiper is registered early
    this.ensureSwiperRegistered();
  }

  ngOnInit(): void {
    // Restore previously selected category from storage (persisted across navigations)
    const savedCategory = this.storage.getSelectedCategory();
    if (savedCategory && savedCategory !== 'all') {
      this.selectedCategoryID = savedCategory;
      // Also restore the tags list for the saved category
      const allTags = this.storage.getTagsData();
      this.tagsList = allTags.filter((tag: any) => tag.category_id === savedCategory);
    }

    this.updateVolumeOptions();
    // this.getMarkets();

    // The mobile tab bar's "Search" tab focuses this page's search input.
    this.storage
      .searchFocus$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.storage.consumeSearchFocusRequest();
        this.focusSearchInput();
      });

    // When bookmark filter is active, instantly remove unbookmarked markets from the list
    this.bookmarkService.bookmarkedMarkets$
      .pipe(takeUntil(this.destroy$))
      .subscribe((bookmarkedSet: Set<string>) => {
        if (this.isBookmarkFilterActive) {
          const before = this.cardList.length;
          this.cardList = this.cardList.filter((card: any) => bookmarkedSet.has(card.id));
          if (this.cardList.length !== before) {
            this.cardRows = this.getCardRows();
          }
        }
      });
  }

  /**
   * Scrolls the search input into view and focuses it, so the mobile keyboard
   * opens straight away.
   * @returns {void}
   */
  private focusSearchInput(): void {
    this.isMobileSearchActive = this.isMobileViewport();
    setTimeout(() => {
      const input: HTMLInputElement | undefined = this.marketSearchInput?.nativeElement;
      if (!input) {
        return;
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
      input.focus();
    });
  }

  /**
   * The expanded search row is a below-576px layout. Negating the desktop query
   * is the exact complement of the stylesheet's (width < 576px), so no
   * fractional width can be mobile here and desktop there.
   * @returns {boolean} True below the mobile breakpoint.
   */
  private isMobileViewport(): boolean {
    return typeof window !== 'undefined' && !window.matchMedia('(min-width: 576px)').matches;
  }

  /**
   * Tapping the field itself opens search the same way the tab bar's "Search"
   * tab does: the row expands and the tab lights up. Mobile only.
   * @returns {void}
   */
  onSearchFocus(): void {
    if (!this.isMobileViewport()) {
      return;
    }
    this.isMobileSearchActive = true;
    this.storage.setSearchTabActive(true);
  }

  /**
   * Ends the expanded state once the user is done typing, which brings the
   * filter and bookmark buttons back. The tab bar's "Search" tab stays lit.
   * @returns {void}
   */
  onSearchDone(): void {
    this.isMobileSearchActive = false;
  }


  getMarkets(isLoadMore: boolean = false): void {
    // Reset pagination when doing a fresh load
    if (!isLoadMore) {
      this.marketsPage = 1;
      this.cardList = [];
      this.hasMoreMarkets = true;
      this.isLoadingMarkets = false;
    }

    if (this.isLoadingMarkets || !this.hasMoreMarkets) {
      return;
    }

    this.isLoadingMarkets = true;
    let filter: any = {
      category_ids: this.selectedCategoryID == '' ? [] : [this.selectedCategoryID],
      tag_ids: this.selectedTagIDs.indexOf('all') > -1 ? [] : this.selectedTagIDs,
      search: this.searchKey?.trim() || '',
      page: this.marketsPage,
      size: this.PAGE_SIZE,
      sort_by: this.selectedSort,
      ...(this.isBookmarkFilterActive && { bookmarked: true }),
      ...(this.selectedFrequency && { frequency: this.selectedFrequency }),
      ...(this.selectedMarketType && { market_type: this.selectedMarketType })
    };
    // console.log('Sending market list request with filter:', filter);
    this.service.getMarketsList(filter)
      .subscribe((result: any) => {
        let newMarkets = result.data?.results || [];

        // console.log('API Result - Markets count:', newMarkets.length, 'Filter active:', this.isBookmarkFilterActive);

        const mappedMarkets: any[] = newMarkets.map((market: any) => {
          let obj: any = {
            cardType: market.outcome_type == 'MULTIPLE' ? 'standard' : market.outcome_type == 'SINGLE' ? 'simple' : 'sports',
            titleText: market.question,
            title: market.question,
            imageUrl: market.image_path,
            id: market.id,
            volume: market.volume,
            frequency: market.frequency,
            options: (market.outcomes || []).map((outcome: any) => ({
              title: outcome.title,
              id: outcome.id,
              percent: outcome.price ? (Number(outcome.price) <= 1 ? Math.round(Number(outcome.price) * 100) : Number(outcome.price)) + '%' : '0%',
              showNested: false,
              hideMain: false
            })),
            winProbability: market.win_probability || 0
          };

          if (market.outcome_type == 'SINGLE') {
            obj.showNested = false;
            obj.hideMain = false;
          }

          return obj;
        });

        let finalMappedMarkets = mappedMarkets;
        if (this.isBookmarkFilterActive) {
          finalMappedMarkets = mappedMarkets.filter(market => this.bookmarkService.isBookmarked(market.id));
        }

        if (isLoadMore) {
          this.cardList = [...this.cardList, ...finalMappedMarkets];
        } else {
          this.cardList = finalMappedMarkets;
        }

        this.cardRows = this.getCardRows();

        // Check if there are more records
        this.hasMoreMarkets = newMarkets.length === this.PAGE_SIZE;
        this.marketsPage++;
        this.isLoadingMarkets = false;
      }, (error: HttpErrorResponse) => {
        console.log(error);
        this.isLoadingMarkets = false;
      })
  }

  // Scroll listener for infinite scroll
  @HostListener('window:scroll')
  onWindowScroll() {
    // The filter dropdown is fixed to the viewport, so it must not outlive a page scroll.
    if (this.openHomeFilterDropdownKey) {
      this.closeHomeFilterDropdown();
    }

    const pos = (window.innerHeight + window.scrollY);
    const max = document.documentElement.scrollHeight;

    // Trigger when within 300px of bottom
    if (pos >= max - 300) {
      if (this.hasMoreMarkets && !this.isLoadingMarkets) {
        this.getMarkets(true);
      }
    }
  }

  // Ensure Swiper is registered
  private ensureSwiperRegistered() {
    try {
      register();
      this.isSwiperReady = true;
    } catch (error) {
      console.warn('Swiper registration error:', error);
      this.isSwiperReady = false;
    }
  }

  setupSwiperListeners() {
    if (!this.cardsSwiper?.nativeElement) {
      console.warn('Swiper element not found');
      return;
    }

    const swiperEl = this.cardsSwiper.nativeElement;

    // Wait for Swiper to be initialized (since init="true")
    const waitForSwiper = () => {
      if (swiperEl.swiper) {
        this.swiperInstance = swiperEl.swiper;
        this.isSwiperInitialized = true;
        // console.log('Swiper instance found:', this.swiperInstance);

        // Add slide change listener
        this.swiperInstance.on('slideChange', () => {
          this.currentCardSlide = this.swiperInstance.realIndex;
        });
      } else {
        // Try again after a short delay
        setTimeout(waitForSwiper, 100);
      }
    };

    waitForSwiper();

    // Alternative: Listen for swiperready event
    swiperEl.addEventListener('swiperready', (event: any) => {
      this.swiperInstance = swiperEl.swiper || event.detail?.[0];
      this.isSwiperInitialized = true;
      console.log('Swiper ready event fired:', this.swiperInstance);

      // Add slide change listener
      if (this.swiperInstance) {
        this.swiperInstance.on('slideChange', () => {
          this.currentCardSlide = this.swiperInstance.realIndex;
        });
      }
    });
  }

  // FIXED: Navigation methods that work with auto-initialized Swiper
  nextCardSlide() {
    if (this.swiperInstance && this.isSwiperInitialized) {
      console.log('Next slide clicked, swiper instance:', this.swiperInstance);
      this.swiperInstance.slideNext();
    } else {
      console.log('Swiper not ready, trying to get instance...');
      // Try to get instance directly
      if (this.cardsSwiper?.nativeElement?.swiper) {
        this.swiperInstance = this.cardsSwiper.nativeElement.swiper;
        this.isSwiperInitialized = true;
        this.swiperInstance.slideNext();
      } else {
        // Fallback: trigger next via element
        this.cardsSwiper.nativeElement.swiper.slideNext();
      }
    }
  }

  prevCardSlide() {
    if (this.swiperInstance && this.isSwiperInitialized) {
      this.swiperInstance.slidePrev();
    } else {
      // Try to get instance directly
      if (this.cardsSwiper?.nativeElement?.swiper) {
        this.swiperInstance = this.cardsSwiper.nativeElement.swiper;
        this.isSwiperInitialized = true;
        this.swiperInstance.slidePrev();
      } else {
        // Fallback: trigger prev via element
        this.cardsSwiper.nativeElement.swiper.slidePrev();
      }
    }
  }

  goToCardSlide(index: number) {
    if (index >= 0 && index < this.totalCardSlides) {
      if (this.swiperInstance && this.isSwiperInitialized) {
        this.swiperInstance.slideTo(index);
      } else if (this.cardsSwiper?.nativeElement?.swiper) {
        this.cardsSwiper.nativeElement.swiper.slideTo(index);
      }
    }
  }


  // Handle window resize
  @HostListener('window:resize')
  onResize() {
    // Growing past the mobile breakpoint restores the ordinary search row.
    if (this.isMobileSearchActive && !this.isMobileViewport()) {
      this.isMobileSearchActive = false;
    }
    // The tag list wraps to its own line at 767px, so its scrollable width changes.
    this.updateArrows();
    this.updateFilterArrows();
    this.closeHomeFilterDropdown();

    if (this.swiperInstance && this.isSwiperInitialized) {
      setTimeout(() => {
        this.swiperInstance.update();
      }, 50);
    }

    this.syncFeatureCarousel();
  }

  // Feature highlight carousel ------------------------------------------------
  // One card per view below 992px, advancing every 3s. The desktop grid neutralises
  // the transform in CSS, so the timer only runs on mobile.
  get featureTrackTransform(): string {
    return `translateX(calc(${-this.featureSlide} * (100% + 1.75rem)))`;
  }

  goToFeatureSlide(index: number) {
    this.featureSlide = index;
    // Restart the countdown so a manually picked card gets a full interval.
    this.startFeatureCarousel();
  }

  private isFeatureCarouselView(): boolean {
    return typeof window !== 'undefined' && window.matchMedia('(max-width: 991px)').matches;
  }

  private startFeatureCarousel() {
    this.stopFeatureCarousel();
    if (!this.isFeatureCarouselView()) {
      return;
    }
    this.featureTimer = setInterval(() => {
      this.featureSlide = (this.featureSlide + 1) % this.featureSlides.length;
    }, this.FEATURE_SLIDE_INTERVAL);
  }

  private stopFeatureCarousel() {
    if (this.featureTimer) {
      clearInterval(this.featureTimer);
      this.featureTimer = null;
    }
  }

  private syncFeatureCarousel() {
    if (this.isFeatureCarouselView()) {
      if (!this.featureTimer) {
        this.startFeatureCarousel();
      }
    } else {
      this.stopFeatureCarousel();
      this.featureSlide = 0;
    }
  }

  scrollRight() {
    this.scrollContainer.nativeElement.scrollBy({
      left: 200,
      behavior: 'smooth'
    });
    this.updateArrows();
  }

  scrollLeft() {
    this.scrollContainer.nativeElement.scrollBy({
      left: -200,
      behavior: 'smooth'
    });
    this.updateArrows();
  }

  onScroll() {
    this.updateArrows();
  }

  updateArrows() {
    if (!this.scrollContainer?.nativeElement) return;

    const el = this.scrollContainer.nativeElement;
    const atStart = el.scrollLeft <= 10;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 10;

    this.showLeftArrow = !atStart;
    this.showRightArrow = !atEnd;
  }

  toggleFilterPanel(event: Event) {
    event.stopPropagation();
    this.isFilterPanelOpen = !this.isFilterPanelOpen;
    if (!this.isFilterPanelOpen) {
      this.closeHomeFilterDropdown();
    } else {
      // The row has no measurable width until the panel is displayed.
      setTimeout(() => this.updateFilterArrows());
    }
  }

  closeFilterPanel() {
    this.isFilterPanelOpen = false;
    this.closeHomeFilterDropdown();
  }

  toggleHomeFilterDropdown(key: 'volume' | 'frequency' | 'status', event: Event) {
    event.stopPropagation();

    if (this.openHomeFilterDropdownKey === key) {
      this.closeHomeFilterDropdown();
      return;
    }

    this.openHomeFilterDropdownKey = key;
    this.positionHomeFilterDropdown(event.currentTarget as HTMLElement);
    // Held still because the list is fixed to the viewport: a page that scrolled
    // underneath would leave it stranded away from the button it belongs to.
    this.scrollLock.set(true);
  }

  private closeHomeFilterDropdown() {
    this.openHomeFilterDropdownKey = null;
    this.homeFilterDropdownPosition = null;
    this.scrollLock.set(false);
  }

  /**
   * Closes an open filter list when the click lands anywhere else. The page is
   * held still while the list is open, so it has to let go on the next click -
   * a list left open would read as a page that has stopped scrolling.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (!this.openHomeFilterDropdownKey) return;

    // Opening a list stops the click from travelling, so anything arriving here
    // is from outside that list
    if (!(event.target as HTMLElement | null)?.closest('.home-filter-dropdown')) {
      this.closeHomeFilterDropdown();
    }
  }

  /**
   * Anchors the fixed dropdown to its trigger and sizes it to the room that is
   * actually left on screen - below the trigger where it fits, above it where
   * there is more room, and never past the fixed mobile tab bar at the bottom.
   * @param trigger - The filter button the list hangs from
   */
  private positionHomeFilterDropdown(trigger: HTMLElement | null) {
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const placement = measureDropdownPlacement(trigger, { maxHeight: FILTER_MENU_CAP });
    const maxLeft = Math.max(FILTER_MENU_EDGE_GAP, window.innerWidth - FILTER_MENU_WIDTH - FILTER_MENU_EDGE_GAP);

    this.homeFilterDropdownPosition = {
      // Growing upwards means pinning the bottom edge instead, since the list's
      // own height is not known until it has rendered
      top: placement.above ? null : rect.bottom + FILTER_MENU_GAP,
      bottom: placement.above ? window.innerHeight - rect.top + FILTER_MENU_GAP : null,
      left: Math.min(Math.max(rect.left, FILTER_MENU_EDGE_GAP), maxLeft),
      maxHeight: placement.maxHeight
    };
  }

  scrollFiltersLeft() {
    this.filterScrollContainer?.nativeElement.scrollBy({ left: -200, behavior: 'smooth' });
  }

  scrollFiltersRight() {
    this.filterScrollContainer?.nativeElement.scrollBy({ left: 200, behavior: 'smooth' });
  }

  onFilterScroll() {
    // A fixed dropdown does not travel with its trigger, so drop it while scrolling.
    this.closeHomeFilterDropdown();
    this.updateFilterArrows();
  }

  updateFilterArrows() {
    const el = this.filterScrollContainer?.nativeElement;
    if (!el) return;

    this.showFilterLeftArrow = el.scrollLeft > 10;
    this.showFilterRightArrow = el.scrollLeft + el.clientWidth < el.scrollWidth - 10;
  }

  toggleBookmarkFilter() {
    // console.log('Bookmark filter clicked, isLoggedIn:', this.auth.isLoggedIn());
    if (!this.auth.isLoggedIn()) {
      console.log('User not logged in, sending login signal');
      this.auth.sendOperationSignal('login');
      return;
    }
    this.isBookmarkFilterActive = !this.isBookmarkFilterActive;
    this.getMarkets(false);
  }

  isHomeFilterDropdownOpen(key: 'volume' | 'frequency' | 'status') {
    return this.openHomeFilterDropdownKey === key;
  }

  selectHomeFilterOption(key: 'volume' | 'frequency' | 'status', value: string, event: Event) {
    event.stopPropagation();
    this.homeFilterDropdowns = this.homeFilterDropdowns.map((filter) =>
      filter.key === key ? { ...filter, selected: value } : filter
    );
    this.closeHomeFilterDropdown();
    // The "Clear Filters" button may appear or disappear, changing the row width.
    setTimeout(() => this.updateFilterArrows());

    // Apply sort filter dynamically using the map
    if (key === 'volume') {
      const sortKey = MARKET_SORT_KEY_MAP[value];
      if (sortKey) {
        this.selectedSort = sortKey;
        this.getMarkets(false);
      }
    }
    // Apply frequency filter
    else if (key === 'frequency') {
      if (value === 'All') {
        this.selectedFrequency = null;
      } else {
        const frequencyKey = MARKET_FREQUENCY_MAP[value];
        if (frequencyKey) {
          this.selectedFrequency = frequencyKey;
        }
      }
      this.getMarkets(false);
    }
    // Apply market type filter
    else if (key === 'status') {
      const marketTypeKey = MARKET_TYPE_MAP[value];
      if (marketTypeKey) {
        this.selectedMarketType = marketTypeKey;
        this.updateVolumeOptions();
      }
      this.getMarkets(false);
    }
  }

  updateVolumeOptions() {
    const statusFilter = this.homeFilterDropdowns.find(d => d.key === 'status');
    const volumeFilter = this.homeFilterDropdowns.find(d => d.key === 'volume');

    if (volumeFilter && statusFilter) {
      if (statusFilter.selected === 'Active') {
        volumeFilter.options = ['24hr Volume', 'Total Volume', 'Liquidity', 'Newest', 'Ending Soon', 'Competitive'];
        // If current selection is 'Closed Time', reset it to 'Newest'
        if (volumeFilter.selected === 'Closed Time') {
          volumeFilter.selected = 'Newest';
          this.selectedSort = MarketSortKey.NEWEST;
        }
      } else if (statusFilter.selected === 'Resolved') {
        volumeFilter.options = ['24hr Volume', 'Closed Time'];
        // If current selection is not valid for Resolved, default to 'Closed Time'
        if (volumeFilter.selected !== '24hr Volume' && volumeFilter.selected !== 'Closed Time') {
          volumeFilter.selected = 'Closed Time';
          this.selectedSort = MarketSortKey.CLOSED_TIME;
        }
      }
    }
  }

  toggleHomeFilterSelection(key: 'sports' | 'crypto' | 'earnings', event: Event) {
    // event.stopPropagation();
    // this.homeFilterToggles = this.homeFilterToggles.map((toggle) =>
    //   toggle.key === key ? { ...toggle, checked: !toggle.checked } : toggle
    // );
  }

  clearHomeFilters(event: Event) {
    event.stopPropagation();
    this.homeFilterDropdowns = HOME_FILTER_DROPDOWNS.map((filter) => ({
      ...filter,
      selected: filter.defaultValue
    }));
    this.selectedSort = MarketSortKey.NEWEST;
    this.selectedFrequency = null;
    this.selectedMarketType = MarketType.ACTIVE;
    this.updateVolumeOptions();
    this.closeFilterPanel();
    this.getMarkets(false);
  }

  get hasActiveHomeFilters(): boolean {
    return this.homeFilterDropdowns.some((filter) => filter.selected !== filter.defaultValue)
    // || this.homeFilterToggles.some((toggle) => toggle.checked);
  }


  rows = [
    { title: '15-17.99%', percent: '11%', showNested: false, hideMain: false },
    { title: '18-21%', percent: '20%', showNested: false, hideMain: false },
    { title: '22-25%', percent: '35%', showNested: false, hideMain: false }
  ];

  toggleYes(index: number) {
    this.rows = this.rows.map((row, i) => {
      if (i === index) {
        return {
          ...row,
          showNested: true,
          hideMain: true
        };
      }
      return {
        ...row,
        showNested: false,
        hideMain: false
      };
    });
  }

  toggleNo(index: number) {
    this.rows[index].showNested = false;
    this.rows[index].hideMain = false;
  }

  closeNested(index: number) {
    this.rows[index].showNested = false;
    this.rows[index].hideMain = false;
  }

  getCardRows() {
    const rows = [];
    for (let i = 0; i < this.cardList.length; i += 4) {
      rows.push(this.cardList.slice(i, i + 4));
    }
    return rows;
  }

  // New methods for option handling
  isAnyOptionActive(): boolean {
    return this.rows.some(row => row.showNested);
  }

  toggleOptionYes(optionIndex: number) {
    this.rows.forEach((row, index) => {
      if (index === optionIndex) {
        row.showNested = true;
        row.hideMain = true;
      } else {
        row.showNested = false;
        row.hideMain = false;
      }
    });
  }

  toggleOptionNo(optionIndex: number) {
    this.toggleOptionYes(optionIndex);
  }

  toggleOption(index: number) {
    const element = event?.target as HTMLElement;

    if (element.classList.contains('yes')) {
      this.toggleOptionYes(index);
    }
    else if (element.classList.contains('no')) {
      this.toggleOptionNo(index);
    }
    else {
      this.toggleOptionYes(index);
    }
  }

  closeOptionNested(optionIndex: number) {
    // Reset all options to default state
    this.rows.forEach(row => {
      row.showNested = false;
      row.hideMain = false;
    });
  }

  displayTags(data: { option: string; category: string; }): void {
    let tags: any[] = this.storage.getTagsData();
    if (data.option == 'dynamic' && data.category !== 'all') {
      this.selectedCategoryID = data.category;
      tags = tags.filter((list: any) => list.category_id == data.category);
    } else {
      this.selectedCategoryID = "";
    }

    this.tagsList = tags;
    this.selectTag('all');
  }

  /**
   * Selects a tag by its ID.
   * If 'all' is selected, it deselects other tags.
   * @param id 
   */
  selectTag(id: string): void {
    let tagids: any[] = [];
    if (id == 'all') {
      tagids = ['all'];
    } else {
      let index: number = this.selectedTagIDs.indexOf(id);
      if (index > -1) {
        // Deselecting the currently selected tag, go back to 'all'
        tagids = ['all'];
      } else {
        // Selecting a new tag, replace everything with this tag
        tagids = [id];
      }
    }

    this.selectedTagIDs = tagids;
    this.getMarkets(false);
  }

  navigateToMarketDetail(event: { id: string, outcome?: string }): void {
    const navigationExtras = event.outcome ? { queryParams: { outcome: event.outcome } } : {};
    this.router.navigate(["/details/" + event.id], navigationExtras)
  }
  closeEnableModal() {
    this.showEnableModal = false;
  }
  ngOnDestroy() {
    this.stopFeatureCarousel();
    // Leaving the page with a filter list open must not strand the lock on <body>
    this.closeHomeFilterDropdown();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
